import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import ICON_CLOSE from '../../../public/static/v4/viewer/icon-close.svg';
import ICON_AUTO_SCROLL_V4_LIGHT from '../../../public/static/v4/viewer/icon-setting-auto-v4-light.svg';
import ICON_AUTO_SCROLL_V4 from '../../../public/static/v4/viewer/icon-setting-auto-v4.svg';
import ICON_COMMENT_V4_LIGHT from '../../../public/static/v4/viewer/icon-setting-comment-v4-light.svg';
import ICON_COMMENT_V4 from '../../../public/static/v4/viewer/icon-setting-comment-v4.svg';
import ICON_LOCATION_V4_LIGHT from '../../../public/static/v4/viewer/icon-setting-location-v4-light.svg';
import ICON_LOCATION_V4 from '../../../public/static/v4/viewer/icon-setting-location-v4.svg';
import ICON_PAGEVIEW_V4_LIGHT from '../../../public/static/v4/viewer/icon-setting-pageview-v4-light.svg';
import ICON_PAGEVIEW_V4 from '../../../public/static/v4/viewer/icon-setting-pageview-v4.svg';
import ICON_SCROLLVIEW_V4_LIGHT from '../../../public/static/v4/viewer/icon-setting-scrollview-v4-light.svg';
import ICON_SCROLLVIEW_V4 from '../../../public/static/v4/viewer/icon-setting-scrollview-v4.svg';
import ICON_SHARE_V4_LIGHT from '../../../public/static/v4/viewer/icon-setting-share-v4-light.svg';
import ICON_SHARE_V4 from '../../../public/static/v4/viewer/icon-setting-share-v4.svg';
import ICON_BGM_V4_LIGHT from '../../../public/static/v4/viewer/icon-setting-sound-v4-light.svg';
import ICON_BGM_V4 from '../../../public/static/v4/viewer/icon-setting-sound-v4.svg';
import Button from '../../common/component/Button';
import Text from '../../common/component/Text';
import {pToast} from '../../common/container/PToast';
import {showShareAlert} from '../../common/container/ShareAlert';
import useTheme from '../../common/hook/useTheme';
import useTypedSelector from '../../common/hook/useTypedSelector';
import {IToast} from '../../common/type';
import {sendViewImp} from '../../common/util/tracking';
import MaxWidth from '../../commonSub/component/MaxWidth';
import SettingItemToggleV4 from '../../commonSub/component/SettingItemToggleV4';
import SettingItemV4 from '../../commonSub/component/SettingItemV4';
import SettingItemWrapperV4 from '../../commonSub/component/SettingItemWrapperV4';
import useViewerSettings from '../../commonSub/hook/useViewerSettings';
import useBlockBodyScroll from '../../home/common/hook/useBlockBodyScroll';
import {sendViewerEvent} from '../common';
import useViewerAudio from '../hook/useViewerAudio';
import {actions} from '../state/actions';

interface IProps {
  contentId: number;
  episodeId: number;
  episodeTitle?: string;
  isStopSales: boolean;
}

export default function Setting({
  contentId,
  episodeId,
  episodeTitle,
  isStopSales,
}: IProps) {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const {theme} = useTheme();

  const [viewerState, canChangeViewType, bgm, isPlayBgm] = useTypedSelector(
    ({viewer}) => [
      viewer.viewerState,
      !!viewer.content?.canChangeViewType,
      viewer.episode?.bgm,
      viewer.isPlayBgm,
    ]
  );

  const [settings, setSetting] = useViewerSettings(contentId);
  const {isPreviewComment, isKeepLastPage, autoScrollSpeed, viewType} =
    settings;
  const isOpen = viewerState === 'setting';
  useBlockBodyScroll(isOpen);

  useEffect(() => {
    if (isOpen) {
      sendViewImp({
        section: 'viewer',
        page: 'viewer',
        customProps: {
          page_content_id: contentId,
          page_episode_id: episodeId,
          event_layer: 'popup',
          viewer_type: viewType,
          popup_type: 'viewer_setting',
        },
        viewImpContents: {
          name: 'popup',
        },
      });
    }
  }, [contentId, episodeId, isOpen, viewType]);

  const onClickClose = () => {
    dispatch(actions.setViewerState('none'));
    sendSettingClickEvent('popup_close');
  };

  const changeViewType = () => {
    const newViewType = viewType === 'scroll' ? 'page' : 'scroll';
    sendSettingClickEvent('popup_view_type', newViewType);
    setSetting('viewType', newViewType);
    pToast({
      toastId: 'viewType',
      desc: t(
        newViewType === 'scroll'
          ? 'viewer_setting_view_scroll_switch_toast'
          : 'viewer_setting_view_page_switch_toast'
      ),
      variant: 'black',
    });
    dispatch(actions.setViewerState('none'));
  };

  const sendSettingClickEvent = (
    actionName: string,
    value?: boolean | string
  ) => {
    sendViewerEvent({
      actionName,
      action: 'click',
      customProps: {
        content_id: contentId,
        episode_id: episodeId,
        viewer_type: viewType,
        event_layer: 'popup',
        popup_type: 'viewer_setting',
        set_value: value,
      },
    });
  };

  const toastOption: Pick<
    IToast,
    'variant' | 'showingDuration' | 'isShowEvery'
  > = {
    variant: 'transparent',
    showingDuration: 1000,
    isShowEvery: true,
  };

  const isEnableBgm = !!bgm;
  const {play, pause} = useViewerAudio(bgm);

  return (
    <div
      className={cn('fixed w-full h-full z-navigationBarPlus1 bg-black/70', {
        hidden: !isOpen,
      })}
      onClick={e => e.stopPropagation()}
    >
      <Button className="!absolute right-18 top-10" onClick={onClickClose}>
        <img src={ICON_CLOSE} alt="close" />
      </Button>
      <MaxWidth
        hasMdWidth
        className="absolute bottom-30 left-1/2 -translate-x-1/2 w-full px-11"
      >
        <SettingItemWrapperV4>
          {!isStopSales && (
            <SettingItemToggleV4
              iconSrc={ICON_MAP[theme].comment}
              topText={t('viewer_setting_preview_comment_button')}
              isChecked={isPreviewComment}
              onChange={() => {
                sendSettingClickEvent(
                  'popup_preview_comment',
                  !isPreviewComment ? 'Y' : 'N'
                );
                setSetting('isPreviewComment', !isPreviewComment);
                pToast({
                  toastId: 'viewerSettingPreviewComment',
                  desc: t(
                    isPreviewComment
                      ? 'viewer_setting_preview_comment_off_text'
                      : 'viewer_setting_preview_comment_on_text'
                  ),
                  ...toastOption,
                });
              }}
            />
          )}
          <SettingItemToggleV4
            iconSrc={ICON_MAP[theme].location}
            topText={t('viewer_setting_remember_button')}
            isChecked={isKeepLastPage}
            onChange={() => {
              sendSettingClickEvent(
                'popup_bookmark',
                !isKeepLastPage ? 'Y' : 'N'
              );
              setSetting('isKeepLastPage', !isKeepLastPage);
              pToast({
                toastId: 'viewerSettingLastViewed',
                desc: t(
                  isKeepLastPage
                    ? 'viewer_setting_last_viewed_off_text'
                    : 'viewer_setting_last_viewed_on_text'
                ),
                ...toastOption,
              });
            }}
          />
          <SettingItemToggleV4
            iconSrc={ICON_MAP[theme].bgm}
            topText={t('viewer_setting_bgm_button')}
            isChecked={isPlayBgm}
            disabled={!isEnableBgm}
            onChange={() => {
              sendSettingClickEvent('popup_bgm', !isPlayBgm ? 'Y' : 'N');
              dispatch(actions.setValue('isPlayBgm', !isPlayBgm));
              if (!isPlayBgm) {
                play();
              } else {
                pause();
              }
              pToast({
                toastId: 'viewerSettingSound',
                desc: t(
                  isPlayBgm
                    ? 'viewer_setting_sound_off_text'
                    : 'viewer_setting_sound_on_text'
                ),
                ...toastOption,
              });
            }}
          />
          {viewType === 'scroll' && (
            <>
              <SettingItemV4
                type="default"
                iconSrc={ICON_MAP[theme].autoScroll}
                topText={t('viewer_setting_autoscroll_button_v4')}
              />
              <div className="pt-4 px-18 pb-18">
                <div className="flex gap-4">
                  {[1, 2, 3, 4].map(speed => (
                    <Button
                      key={speed}
                      className="flex-1"
                      onClick={() => {
                        sendSettingClickEvent(
                          'popup_auto_scroll',
                          String(speed)
                        );
                        setSetting('autoScrollSpeed', speed);
                      }}
                    >
                      <Text
                        className={cn('s14-medium-grey06', {
                          '!text-white-black': autoScrollSpeed === speed,
                        })}
                      >
                        {speed}x
                      </Text>
                      <div
                        className={cn('h-6 mt-4 rounded-10 bg-grey-03', {
                          '!bg-red': autoScrollSpeed === speed,
                        })}
                      />
                    </Button>
                  ))}
                </div>
              </div>
            </>
          )}
        </SettingItemWrapperV4>
        <SettingItemWrapperV4 className="mt-8">
          {canChangeViewType && (
            <SettingItemV4
              type="default"
              iconSrc={
                viewType === 'scroll'
                  ? ICON_MAP[theme].pageview
                  : ICON_MAP[theme].scrollview
              }
              topText={
                viewType === 'scroll'
                  ? t('viewer_setting_view_page_button')
                  : t('viewer_setting_view_scroll_button')
              }
              onClick={changeViewType}
            />
          )}
          {!isStopSales && (
            <SettingItemV4
              type="default"
              iconSrc={ICON_MAP[theme].share}
              topText={t('viewer_setting_share_ep_button')}
              onClick={() => {
                sendSettingClickEvent('popup_share');
                showShareAlert({
                  text: episodeTitle,
                });
              }}
            />
          )}
        </SettingItemWrapperV4>
      </MaxWidth>
    </div>
  );
}

export const AUTO_SCROLL_SPEED_OPTION_KEY = 'viewer-autoScrollSpeed';
const ICON_MAP = {
  dark: {
    comment: ICON_COMMENT_V4,
    location: ICON_LOCATION_V4,
    bgm: ICON_BGM_V4,
    autoScroll: ICON_AUTO_SCROLL_V4,
    pageview: ICON_PAGEVIEW_V4,
    scrollview: ICON_SCROLLVIEW_V4,
    share: ICON_SHARE_V4,
  },
  light: {
    comment: ICON_COMMENT_V4_LIGHT,
    location: ICON_LOCATION_V4_LIGHT,
    bgm: ICON_BGM_V4_LIGHT,
    autoScroll: ICON_AUTO_SCROLL_V4_LIGHT,
    pageview: ICON_PAGEVIEW_V4_LIGHT,
    scrollview: ICON_SCROLLVIEW_V4_LIGHT,
    share: ICON_SHARE_V4_LIGHT,
  },
};
